import { FC, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, TreeSelect, Select, Tag } from '@maxtropy/components';
import styles from '../index.module.scss';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2EnergyAnalysisListByOuPost,
  V2EnergyAnalysisListByOuPostResponse,
  apiV2EnergyUnitAddGroupPost,
  apiV2EnergyUnitUpdateGroupPost,
  apiV2EnergyAnalysisTreePost,
  V2EnergyAnalysisTreePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { editProps } from '..';

export type GroupItem = Exclude<V2EnergyAnalysisTreePostResponse['list'], undefined>[number];
interface EnergyNode {
  key?: number;
  value?: number;
  name?: string;
  children?: GroupItem[];
}

interface AddCabinetModalProps {
  onClose: () => void;
  updateFn?: () => void;
  visible: boolean;
  cabinet?: editProps;
}
// 递归收集所有子节点 id
const getDescendantIds = (node: GroupItem): number[] => {
  if (!node?.children || node.children.length === 0) return [];
  const res: number[] = [];
  for (const child of node.children) {
    res.push(child.energyUnitId!);
    res.push(...getDescendantIds(child));
  }
  return res;
};

const transformToTreeData = (
  list: GroupItem[],
  onSelectAll: (node: GroupItem, select: boolean) => void,
  checkedValues: number[]
): EnergyNode[] =>
  list.map(item => {
    const descendantIds = getDescendantIds(item);
    const hasDescendants = descendantIds.length > 0;
    const isAllSelected = hasDescendants && descendantIds.every(id => checkedValues?.includes(id));

    return {
      title: (
        <Space size={6}>
          <span>{item.name}</span>
          {hasDescendants && (
            <Tag
              border="solid"
              color={isAllSelected ? 'info' : 'default'}
              onClick={e => {
                e.stopPropagation(); // 阻止触发节点的选中/展开行为
                e.preventDefault();
                onSelectAll(item, !isAllSelected);
              }}
            >
              全选子节点
            </Tag>
          )}
        </Space>
      ),
      value: item.energyUnitId,
      name: item.name,
      key: item.energyUnitId,
      children:
        item.children && item.children.length > 0
          ? transformToTreeData(item.children, onSelectAll, checkedValues)
          : undefined,
    };
  });

const AddGroup: FC<AddCabinetModalProps> = ({ cabinet, onClose, updateFn, visible }) => {
  const [form] = Form.useForm();
  const [unitGroup, setUnitGroup] = useState<V2EnergyAnalysisListByOuPostResponse['list']>([]);
  const [energyUnitIds, setEnergyUnitIds] = useState<number[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<number[]>();
  const groupId = Form.useWatch('energyUnitGroupId', form);

  useEffect(() => {
    const energyUnitList = cabinet?.energyUnitList?.map(i => i.energyUnitId);
    form.setFieldsValue({
      groupName: cabinet?.groupName,
      energyUnitGroupId: cabinet?.energyUnitGroupId,
      energyUnitIdList: energyUnitList,
    });
  }, [cabinet]);

  const { data: unitTreeData } = useRequest(
    () =>
      apiV2EnergyAnalysisTreePost({
        energyUnitGroupId: groupId,
      }).then(res => {
        return res.list ?? [];
      }),
    {
      refreshDeps: [groupId],
    }
  );

  const modalTitle = useMemo(() => {
    return !cabinet ? '新建分组' : '编辑分组';
  }, [cabinet]);

  const handleSelectAll = (node: GroupItem, select: boolean) => {
    const allDesc = getDescendantIds(node);
    if (allDesc.length === 0) return;
    const prevSet = new Set(energyUnitIds.map(v => Number(v)));
    if (select) {
      allDesc.forEach(id => prevSet.add(id));
    } else {
      allDesc.forEach(id => prevSet.delete(id));
    }
    const newEnergyUnitIds = Array.from(prevSet);
    setEnergyUnitIds(newEnergyUnitIds);
    form.setFieldValue('energyUnitIdList', newEnergyUnitIds);
  };

  useMemo(() => {
    if (!unitTreeData) return;
    const keys: number[] = [];
    unitTreeData.forEach(node => {
      keys.push(node.energyUnitId!);
      node.children?.forEach(child => keys.push(child.energyUnitId!));
    });
    setExpandedKeys(keys);
  }, [unitTreeData]);

  const treeData = useMemo(
    () => transformToTreeData(unitTreeData ?? [], handleSelectAll, energyUnitIds),
    [unitTreeData, energyUnitIds]
  );
  const handleExpand = (keys: React.Key[]) => setExpandedKeys(keys as number[]);

  const onOk = () => {
    form
      .validateFields()
      .then(values => {
        const { groupName, energyUnitGroupId, energyUnitIdList } = values;
        const energyUnitIds = energyUnitIdList.map((i: any) => i.value ?? i);
        if (!cabinet) {
          apiV2EnergyUnitAddGroupPost({
            groupName,
            energyUnitGroupId,
            energyUnitIdList: energyUnitIds,
          }).then(res => {
            updateFn?.();
            onClose();
          });
        } else {
          apiV2EnergyUnitUpdateGroupPost({
            groupId: cabinet.id,
            groupName,
            energyUnitGroupId,
            energyUnitIdList: energyUnitIds,
          }).then(res => {
            updateFn?.();
            onClose();
          });
        }
      })
      .catch(err => {});
  };

  useEffect(() => {
    apiV2EnergyAnalysisListByOuPost({}).then(res => {
      setUnitGroup(res.list ?? []);
    });
  }, []);

  return (
    <Modal title={modalTitle} open={visible} onOk={onOk} contentClassName="modal-form-content" onCancel={onClose}>
      <Form form={form}>
        <Form.Item label="分组名称" name="groupName" rules={[{ required: true }]}>
          <Input allowClear placeholder="请输入分组名称" />
        </Form.Item>
        <Form.Item label="用能分析组" name="energyUnitGroupId" rules={[{ required: true }]}>
          <Select
            allowClear
            placeholder="请选择"
            options={unitGroup?.map(i => ({
              label: i.name,
              value: i.id,
            }))}
            onChange={val => {
              form.setFieldValue('energyUnitIdList', []);
            }}
          />
        </Form.Item>
        <Form.Item label="用能单元" name="energyUnitIdList" rules={[{ required: true, message: '请选择用能单元' }]}>
          <TreeSelect
            popupClassName={styles.treeSelect}
            treeData={treeData}
            checkAllAble={true}
            maxTagCount={'responsive'}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_ALL}
            treeCheckStrictly={true}
            onTreeExpand={handleExpand}
            treeExpandedKeys={expandedKeys}
            treeNodeLabelProp="name"
            placeholder="请选择"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddGroup;
